import React, { useEffect, useMemo, useState } from 'react'
import { GridProps } from './types'
import GridItem from '../GridItem'
import {
  FocusContext,
  KeyPressDetails,
  setFocus,
  useFocusable,
} from '@noriginmedia/norigin-spatial-navigation'
import { platformTransitionStyle, throttle } from '../../../../shared/helpers'
import { getGridLayout } from './utils'
const GRID_CONTAINER_FOCUS_KEY = 'GRID_CONTAINER'
const GRID_ITEM_FOCUS_KEY_PREFIX = 'GRID-ITEM-'
const FIRST_GRID_ITEM_FOCUS_KEY = `${GRID_ITEM_FOCUS_KEY_PREFIX}0`
const TRAVERSAL_INTEGER = 4
function Grid({ collections, title }: GridProps) {
  const { ref, focusKey } = useFocusable({
    focusKey: GRID_CONTAINER_FOCUS_KEY,
  })
  const [translateY, setTranslateY] = useState(0)
  const layout = getGridLayout()
  const totalHeight = layout.height + layout.marginBottom

  useEffect(() => {
    setFocus(FIRST_GRID_ITEM_FOCUS_KEY)
  }, [])

  const onArrowPress = (
    direction: string,
    props: { parentOffsetTop: number; index: number },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _details: KeyPressDetails
  ) => {
    const { parentOffsetTop, index } = props
    const maxRowNavigation = Math.ceil(
      collections?.content.length / TRAVERSAL_INTEGER
    )
    const currentRow = Math.floor(index / TRAVERSAL_INTEGER) + 1
    const canNavigateDown = currentRow < maxRowNavigation
    const canNavigateUp = currentRow > 1
    const onLastColumnItem = (index + 1) % TRAVERSAL_INTEGER === 0
    const onFirstColumnItem = index % TRAVERSAL_INTEGER === 0
    const isLastItem = index === collections.content.length - 1
    const isFirstItem = index === 0
    if (direction === 'down' && canNavigateDown) {
      setTranslateY((prevTranslateY) => prevTranslateY - totalHeight)
    }
    if (direction === 'up' && canNavigateUp) {
      setTranslateY((prevTranslateY) =>
        prevTranslateY === 0 ? 0 : prevTranslateY + totalHeight
      )
    }
    if (direction === 'right' && onLastColumnItem && !isLastItem) {
      setTranslateY(-parentOffsetTop)
      setFocus(`${GRID_ITEM_FOCUS_KEY_PREFIX}${index + 1}`)
      return false
    }
    if (direction === 'right' && isLastItem) {
      return false
    }
    if (direction === 'left' && onFirstColumnItem && !isFirstItem) {
      setTranslateY(-parentOffsetTop + totalHeight)
      setFocus(`${GRID_ITEM_FOCUS_KEY_PREFIX}${index - 1}`)
      return false
    }
    return true
  }
  const handleWheel = useMemo(
    () =>
      throttle((event: React.WheelEvent<HTMLDivElement>) => {
        const totalRows = Math.ceil(
          collections?.content.length / TRAVERSAL_INTEGER
        )

        if (totalRows <= 2) {
          event.preventDefault()
          return
        }

        const deltaY = event.deltaY
        if (deltaY > 0) {
          setTranslateY((prev) =>
            Math.max(
              -ref.current.getBoundingClientRect().height + 500,
              prev - 500
            )
          )
        } else if (deltaY < 0) {
          setTranslateY((prev) => Math.min(0, prev + 500))
        }
      }, 600),
    [collections?.content.length]
  )

  return (
    <FocusContext.Provider value={focusKey}>
      <div
        className="collection-screen__grid-container"
        ref={ref}
        style={{
          transition: platformTransitionStyle('transform 400ms ease-in-out'),
          transform: `translateY(${translateY}px)`,
        }}
        onWheel={handleWheel}
      >
        {collections.content.length > 0 && (
          <>
            <div className="collection-screen__grid-header">
              <h2>{title}</h2>
            </div>
            <div className="collection-screen__grid-slider">
              {collections.content.map((collectionContent, index) => (
                <GridItem
                  index={index}
                  key={`${GRID_ITEM_FOCUS_KEY_PREFIX}${index}`}
                  focusKey={`${GRID_ITEM_FOCUS_KEY_PREFIX}${index}`}
                  content={collectionContent}
                  onArrowPress={onArrowPress}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </FocusContext.Provider>
  )
}

export default Grid
